// Original 16-bar instrumental. Synthesized locally; no recordings, samples,
// services or copyrighted tracks. Web Audio schedules every note sample-accurately.
export const NIGHT_PULSE_BPM = 104;
export const RPG_TRACK_TITLE = "Velvet Drift";
const midi = note => 440 * 2 ** ((note - 69) / 12);

export async function composeNightPulse() {
  const OfflineContext = window.OfflineAudioContext || window.webkitOfflineAudioContext;
  if (!OfflineContext) throw new Error("This browser does not support the music player.");
  const beat = 60 / NIGHT_PULSE_BPM;
  const duration = beat * 64;
  const ctx = new OfflineContext(2, Math.ceil(duration * 44100), 44100);
  const bus = ctx.createDynamicsCompressor();
  bus.threshold.value = -16; bus.knee.value = 20; bus.ratio.value = 3;
  bus.attack.value = 0.015; bus.release.value = 0.22;
  // Remove sub-bass rumble; the rhythm is a light shuffle, not a pulsing kick.
  const lowCut = ctx.createBiquadFilter();
  lowCut.type = "highpass"; lowCut.frequency.value = 75; lowCut.Q.value = 0.5;
  bus.connect(lowCut).connect(ctx.destination);

  let seed = 91741;
  const noise = () => { seed = (seed * 16807) % 2147483647; return seed / 1073741823.5 - 1; };
  const hiss = ctx.createBuffer(1, 44100, 44100);
  const samples = hiss.getChannelData(0);
  for (let i = 0; i < samples.length; i++) samples[i] = noise();
  const reverb = ctx.createConvolver();
  const impulse = ctx.createBuffer(2, 66150, 44100);
  for (let channel = 0; channel < 2; channel++) {
    const values = impulse.getChannelData(channel);
    for (let i = 0; i < values.length; i++) values[i] = noise() * (1 - i / values.length) ** 3 * 0.3;
  }
  reverb.buffer = impulse;
  const wet = ctx.createGain(); wet.gain.value = 0.18;
  reverb.connect(wet).connect(bus);

  const note = (frequency, time, length, level, type = "triangle", cutoff = 900, pan = 0) => {
    const osc = ctx.createOscillator(); osc.type = type; osc.frequency.value = frequency;
    const filter = ctx.createBiquadFilter(); filter.type = "lowpass"; filter.frequency.value = cutoff;
    const gain = ctx.createGain();
    const attack = length > beat * 2 ? 0.35 : Math.min(0.07, length * 0.15);
    gain.gain.setValueAtTime(0, time);
    gain.gain.linearRampToValueAtTime(level, time + attack);
    gain.gain.exponentialRampToValueAtTime(0.00001, time + length);
    const stereo = ctx.createStereoPanner(); stereo.pan.value = pan;
    osc.connect(filter).connect(gain).connect(stereo).connect(bus);
    if (frequency > 150) stereo.connect(reverb);
    osc.start(time); osc.stop(time + length + 0.01);
  };
  const percussion = (time, length, level, frequency) => {
    const source = ctx.createBufferSource(); source.buffer = hiss;
    const filter = ctx.createBiquadFilter(); filter.type = "bandpass";
    filter.frequency.value = frequency; filter.Q.value = 0.7;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0, time);
    gain.gain.linearRampToValueAtTime(level, time + 0.012);
    gain.gain.exponentialRampToValueAtTime(0.00001, time + length);
    source.connect(filter).connect(gain).connect(bus);
    source.start(time); source.stop(time + length + 0.01);
  };
  const chords = [[50, 53, 57, 64], [46, 50, 53, 60], [53, 57, 60, 67], [48, 52, 55, 62]];
  for (let bar = 0; bar < 16; bar++) {
    const start = bar * beat * 4;
    const chord = chords[Math.floor(bar / 4)];
    // Warm chord wash and a sustained, quieter bass tone instead of repeated thumps.
    if (bar % 2 === 0) chord.forEach((n, i) => note(midi(n), start + 0.02, beat * 7.85, 0.028, "triangle", 1150, i % 2 ? 0.23 : -0.23));
    note(midi(chord[0] - 12), start + 0.08, beat * 3.8, 0.032, "triangle", 350);
    for (let step = 0; step < 16; step++) {
      const time = start + step * beat / 4 + (step % 2 ? 0.012 : 0) + 0.006;
      // Soft brushed hits and shuffled shakers. No pitched bass drum at all.
      if (step === 0 || step === 10) percussion(time, 0.065, 0.018, 520);
      if (step % 4 === 2) percussion(time, 0.07, 0.019, 5200);
      if ((step === 7 || step === 15) && bar % 2 === 1) percussion(time, 0.04, 0.008, 4400);
      if (step === 4 || step === 12) percussion(time, 0.14, 0.018, 1800);
      if ([3, 10, 15].includes(step) && bar % 4 !== 3) {
        const n = chord[(step + bar) % chord.length] + 12;
        note(midi(n), time, beat * 1.1, 0.023, "sine", 2000, step === 3 ? -0.3 : 0.3);
      }
    }
  }
  const buffer = await ctx.startRendering();
  // A short zero-crossfade at the loop seam prevents clicks; never amplify peaks.
  let peak = 0;
  for (let c = 0; c < buffer.numberOfChannels; c++) {
    const values = buffer.getChannelData(c);
    for (const value of values) peak = Math.max(peak, Math.abs(value));
  }
  const scale = Math.min(1, 0.82 / Math.max(peak, 0.001));
  const fade = Math.round(buffer.sampleRate * 0.012);
  for (let c = 0; c < buffer.numberOfChannels; c++) {
    const values = buffer.getChannelData(c);
    for (let i = 0; i < values.length; i++) values[i] *= scale * Math.min(1, i / fade, (values.length - 1 - i) / fade);
  }
  return buffer;
}